import { useEffect, useMemo, useRef, useState } from "react";
import { Sunrise, Sunset, Plus, Trash2, Search, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * AM / PM Care Roster panel — admin-curated list of staff who stay before or
 * after camp hours and get paid extra. The roster appears on the EOD report's
 * "AM / PM Care" tab so admins can verify each person's sign-out time.
 *
 * UX: type a name → typeahead from /staff-cards search → pick a care_type
 * (AM / PM / Both) → Add. Each row has a chip showing care_type + a
 * delete button. List persists in MongoDB.
 */
const CARE_OPTIONS = [
  { value: "am", label: "AM Care" },
  { value: "pm", label: "PM Care" },
  { value: "both", label: "Both AM + PM Care" },
];

function CareTypeChip({ value }) {
  if (value === "am") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold tracking-wide bg-amber-100 text-amber-900"><Sunrise className="w-3 h-3" /> AM</span>;
  if (value === "pm") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold tracking-wide bg-indigo-100 text-indigo-900"><Sunset className="w-3 h-3" /> PM</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold tracking-wide bg-emerald-100 text-emerald-900"><Sunrise className="w-3 h-3" /><Sunset className="w-3 h-3" /> AM + PM</span>;
}

export default function CareRosterPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Typeahead state
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [selected, setSelected] = useState(null); // {crew_id, name, division}
  const [careType, setCareType] = useState("both");
  const dropRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/care-roster");
      setItems(r.data.items || []);
    } catch {
      toast.error("Failed to load AM/PM Care roster");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Debounced staff search. Hits /staff-cards?search= which already filters
  // by name/phone/division. We flatten the grouped response into a flat list
  // of candidate rows.
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggest(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/staff-cards", { params: { search: query.trim() } });
        const flat = [];
        for (const g of r.data.groups || []) {
          for (const m of g.members || []) {
            flat.push({
              crew_id: m.crew_id,
              name: m.name,
              division: m.division,
              position: m.position,
            });
          }
        }
        const onRoster = new Set(items.map((i) => i.crew_id));
        setSuggestions(flat.filter((m) => !onRoster.has(m.crew_id)).slice(0, 12));
        setShowSuggest(true);
      } catch {
        setSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query, items]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowSuggest(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (m) => {
    setSelected(m);
    setQuery(m.name || "");
    setShowSuggest(false);
  };

  const add = async () => {
    if (!selected?.crew_id) {
      toast.error("Pick a staff member from the dropdown");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/care-roster", {
        crew_id: selected.crew_id,
        care_type: careType,
      });
      toast.success(`Added ${selected.name} to ${CARE_OPTIONS.find((o) => o.value === careType)?.label}`);
      setSelected(null);
      setQuery("");
      setCareType("both");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Remove ${it.name} from AM/PM Care roster?`)) return;
    try {
      await api.delete(`/admin/care-roster/${encodeURIComponent(it.crew_id)}`);
      toast.success(`Removed ${it.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  const changeType = async (it, newType) => {
    try {
      await api.post("/admin/care-roster", { crew_id: it.crew_id, care_type: newType });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const grouped = useMemo(() => {
    const out = { am: [], pm: [], both: [] };
    for (const it of items) {
      const k = it.care_type === "am" || it.care_type === "pm" ? it.care_type : "both";
      out[k].push(it);
    }
    return out;
  }, [items]);

  return (
    <section className="p-6" data-testid="care-roster-section">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <Sunrise className="w-5 h-5 text-amber-600" /><Sunset className="w-5 h-5 text-indigo-600" /> AM / PM Care Roster
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Staff who stay before or after camp hours and get paid extra. They still appear on the main attendance dashboard.
            On the End-of-Day report, an &ldquo;AM / PM Care&rdquo; tab shows just these people so you can verify their sign-out time.
          </p>
        </div>
      </div>

      {/* Add row */}
      <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_auto] gap-3">
          {/* Typeahead */}
          <div className="relative" ref={dropRef}>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Staff name</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={query}
                placeholder="Start typing a name…"
                onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
                onFocus={() => suggestions.length > 0 && setShowSuggest(true)}
                className="w-full pl-9 pr-9 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="care-search-input"
                autoComplete="off"
              />
              {query && (
                <button
                  onClick={() => { setQuery(""); setSelected(null); setShowSuggest(false); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                  data-testid="care-search-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {showSuggest && suggestions.length > 0 && (
              <ul
                className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg"
                data-testid="care-search-suggestions"
              >
                {suggestions.map((m) => (
                  <li key={m.crew_id}>
                    <button
                      onClick={() => pick(m)}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 last:border-b-0"
                      data-testid={`care-suggest-${m.crew_id}`}
                    >
                      <div className="font-semibold text-slate-900 text-sm">{m.name}</div>
                      <div className="text-xs text-slate-500">
                        {m.division || "—"}{m.position ? ` · ${m.position}` : ""}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {showSuggest && query.trim().length >= 2 && suggestions.length === 0 && (
              <div className="absolute z-20 mt-1 w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs text-slate-500">
                No matches{items.find((i) => (i.name || "").toLowerCase().includes(query.trim().toLowerCase())) ? " (already on roster)" : ""}.
              </div>
            )}
          </div>
          {/* Care type */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Care type</label>
            <select
              value={careType}
              onChange={(e) => setCareType(e.target.value)}
              className="w-full py-2 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="care-type-select"
            >
              {CARE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          {/* Add */}
          <div className="flex md:items-end">
            <button
              onClick={add}
              disabled={busy || !selected}
              className="w-full md:w-auto btn-touch inline-flex items-center justify-center gap-2 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold"
              data-testid="care-add-btn"
            >
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
        </div>
        {selected && (
          <div className="mt-2 text-xs text-slate-600">
            Selected: <span className="font-semibold text-slate-900">{selected.name}</span>
            {selected.division ? ` · ${selected.division}` : ""}
          </div>
        )}
      </div>

      {/* List */}
      <div className="mt-6">
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500">Loading roster…</div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500" data-testid="care-empty">
            No one on the AM/PM Care roster yet. Use the search above to add staff.
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Name</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Division</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Care</th>
                  <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.crew_id} className="border-t border-slate-100" data-testid={`care-row-${it.crew_id}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900">{it.name}</td>
                    <td className="px-4 py-3 text-slate-600">{it.division || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <CareTypeChip value={it.care_type} />
                        <select
                          value={it.care_type}
                          onChange={(e) => changeType(it, e.target.value)}
                          className="text-xs py-1 px-2 rounded-md border border-slate-200 bg-white"
                          data-testid={`care-type-edit-${it.crew_id}`}
                        >
                          {CARE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => remove(it)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200"
                        data-testid={`care-remove-${it.crew_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="mt-3 text-xs text-slate-500">
            {grouped.am.length} AM · {grouped.pm.length} PM · {grouped.both.length} Both · {items.length} total
          </div>
        )}
      </div>
    </section>
  );
}
